import { loadQuery, type PreloadedQuery } from 'react-relay';
import { createOperationDescriptor, type CacheConfig, type IEnvironment } from 'relay-runtime';

import query, {
	type ViewPagePersonalizationQuery as PersonalizationQuery,
} from './__generated__/ViewPagePersonalizationQuery.graphql.ts';
import { buildViewPagePersonalizationVariables } from './viewPagePersonalizationVariables.ts';
import { PersonalizationQueryReference } from './PersonalizationQueryReference.ts';

type Variables = PersonalizationQuery['variables'];
export type PersonalizationReaderReference = {
	reference: PreloadedQuery<PersonalizationQuery>;
	variables: Variables;
	disposed: boolean;
};
type Entry = {
	execution: PersonalizationQueryReference;
	views: PersonalizationReaderReference[];
	pendingReaders: Set<PersonalizationReaderReference>;
	readers: number;
	unclaimed: boolean;
};

/** Environment-scoped ownership for independent executions, never a shared readiness promise. */
export class PersonalizationQueryOwner {
	private entries = new Map<string, Entry>();
	private allEntries = new Set<Entry>();
	private readers = new Map<string, PersonalizationReaderReference>();
	private entryByReader = new WeakMap<PersonalizationReaderReference, Entry>();
	private hydrationCleanup: (() => void) | undefined;

	constructor(private readonly environment: IEnvironment) {}

	get(variables: Variables): PersonalizationReaderReference | undefined {
		return this.readers.get(createOperationDescriptor(query, variables).request.identifier);
	}

	preload(
		variables: Variables,
		config?: CacheConfig,
		storeOnly = false,
	): PersonalizationQueryReference {
		const identifier = createOperationDescriptor(query, variables).request.identifier;
		const existing = this.entries.get(identifier);
		if (existing && !existing.execution.failed && !existing.execution.disposed) {
			if (
				!existing.execution.settled ||
				this.environment.check(existing.execution.operation).status === 'available'
			) {
				return existing.execution;
			}
		}
		const execution = new PersonalizationQueryReference(
			this.environment,
			variables,
			config,
			storeOnly,
		);
		const entry: Entry = {
			execution,
			views: [],
			pendingReaders: new Set(),
			readers: 0,
			unclaimed: true,
		};
		this.entries.set(identifier, entry);
		this.allEntries.add(entry);
		// Cache-only hydration must publish references synchronously before React starts.
		if (execution.settled) this.publish(entry);
		else void execution.ready.then(() => this.publish(entry));
		return execution;
	}

	private publish(entry: Entry): void {
		const { execution } = entry;
		if (execution.disposed || execution.failed || execution.hasUnscopedErrors) return;
		const variables = execution.variables;
		const identity = {
			cloudId: variables.cloudId,
			contentId: variables.contentId,
			spaceKey: variables.spaceKey,
			isBlog:
				variables.catchupContentType === 'BLOGPOST' ||
				variables.recommendationEntityType === 'blogpost',
		};
		// Readers select only their group. A Loom field error cannot poison a presence read.
		const groups = [
			...(variables.includePresenceSettings ? [{ presence: true }] : []),
			...(variables.includeRecommendations
				? [{ recommendations: { experience: variables.recommendationExperience } }]
				: []),
			...(variables.includeAIFloatingContextMenu ? [{ catchup: true }] : []),
		];
		for (const group of groups) {
			const selected = buildViewPagePersonalizationVariables(identity, group);
			const view: PersonalizationReaderReference = {
				variables: selected,
				reference: loadQuery<PersonalizationQuery>(this.environment, query, selected, {
					fetchPolicy: 'store-only',
				}),
				disposed: false,
			};
			entry.views.push(view);
			entry.pendingReaders.add(view);
			this.entryByReader.set(view, entry);
			this.readers.set(createOperationDescriptor(query, selected).request.identifier, view);
		}
		this.notify();
	}

	retain(view: PersonalizationReaderReference): () => void {
		const entry = this.entryByReader.get(view);
		if (!entry || view.disposed) return () => {};
		entry.readers++;
		entry.pendingReaders.delete(view);
		entry.unclaimed = entry.pendingReaders.size > 0;
		let released = false;
		return () => {
			if (released) return;
			released = true;
			entry.readers--;
			queueMicrotask(() => {
				if (!entry.readers && !entry.unclaimed) this.remove(entry);
			});
		};
	}

	/** Called at navigation start, including navigation away and cancellation by a newer route. */
	beginPreload(): void {
		this.hydrationCleanup?.();
		this.hydrationCleanup = undefined;
		for (const entry of this.allEntries) {
			entry.unclaimed = false;
			entry.pendingReaders.clear();
			if (!entry.readers) this.remove(entry);
		}
	}

	getHydrationVariables(): Variables[] {
		const variables: Variables[] = [];
		for (const { execution } of this.entries.values()) {
			if (
				execution.settled &&
				!execution.disposed &&
				!execution.failed &&
				!execution.hasUnscopedErrors
			) {
				variables.push(execution.variables);
			}
		}
		return variables;
	}

	setHydrationCleanup(cleanup: () => void): void {
		this.hydrationCleanup?.();
		this.hydrationCleanup = cleanup;
	}

	private remove(entry: Entry): void {
		this.allEntries.delete(entry);
		const identifier = entry.execution.operation.request.identifier;
		if (this.entries.get(identifier) === entry) this.entries.delete(identifier);
		for (const view of entry.views) {
			const key = createOperationDescriptor(query, view.variables).request.identifier;
			if (this.readers.get(key) === view) this.readers.delete(key);
			view.disposed = true;
			view.reference.dispose();
		}
		entry.execution.dispose();
		this.notify();
	}

	private notify(): void {
		listeners.get(this.environment)?.forEach((listener) => listener());
	}
}

const owners = new WeakMap<IEnvironment, PersonalizationQueryOwner>();
const listeners = new WeakMap<IEnvironment, Set<() => void>>();

export function getPersonalizationQueryOwner(
	environment: IEnvironment,
): PersonalizationQueryOwner | undefined {
	return owners.get(environment);
}

/** Preload/bootstrap only: never create owners or query references in React render. */
export function getOrCreatePersonalizationQueryOwner(
	environment: IEnvironment,
): PersonalizationQueryOwner {
	let owner = owners.get(environment);
	if (!owner) {
		owner = new PersonalizationQueryOwner(environment);
		owners.set(environment, owner);
	}
	return owner;
}

export function subscribePersonalizationQueryOwner(
	environment: IEnvironment,
	listener: () => void,
): () => void {
	let subscriptions = listeners.get(environment);
	if (!subscriptions) {
		subscriptions = new Set();
		listeners.set(environment, subscriptions);
	}
	subscriptions.add(listener);
	return () => {
		subscriptions.delete(listener);
	};
}
